import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Share2, MessageCircle, Gift, Users, Award } from "lucide-react";

export default function ReferralCard() {
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/referrals/mine", { withCredentials: true })
      .then(({ data }) => setR(data))
      .catch(() => setR(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="card-surface p-8 text-center text-asure-text2">Loading rewards…</div>;
  if (!r) return null;

  const shareUrl = `${window.location.origin}/?ref=${r.referral_code}`;
  const shareMsg = `I've been loving ASURE for daily nutrition. Use my link ${shareUrl} to enquire and try the 30-day daily nutrition ritual. ASURE — Better Every Day.`;

  const copyLink = async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); } catch { toast.error("Copy failed"); } };
  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareMsg)}`, "_blank");
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "ASURE — Better Every Day.", text: shareMsg, url: shareUrl }); } catch {}
    } else { copyLink(); }
  };

  return (
    <section className="mt-12">
      <div className="rounded-3xl overflow-hidden border border-[color:var(--asure-border)]" style={{ background: "linear-gradient(0deg, var(--asure-forest), var(--asure-deep))" }}>
        <div className="p-8 md:p-10 text-white">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="eyebrow" style={{ color: "var(--asure-gold)" }}>ASURE Rewards</div>
              <h3 className="font-display text-3xl md:text-4xl mt-2 leading-tight">Refer a friend.<br/>Get a free bottle.</h3>
              <p className="mt-3 text-white/80 text-sm max-w-md">Share your ASURE link. When a friend enquires and completes their order, you get a free bottle on your next delivery.</p>
            </div>
            <div className="flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-2 text-xs backdrop-blur">
              <Gift size={14} style={{ color: "var(--asure-gold)" }} /> 1 conversion = 1 free bottle
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-3">
            <Stat icon={Users} label="Enquiries" v={r.total_enquiries} />
            <Stat icon={Award} label="Conversions" v={r.converted} />
            <Stat icon={Gift} label="Free Bottles Earned" v={r.rewards_earned} highlight />
          </div>

          <div className="mt-8 bg-white/95 text-asure-text rounded-2xl p-4 md:p-5 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="eyebrow">Your referral link</div>
              <div className="mt-1 text-sm md:text-base font-mono truncate select-all" data-testid="referral-link">{shareUrl}</div>
              <div className="mt-1 text-xs text-asure-text2">Your code: <span className="font-semibold text-asure-forest">{r.referral_code}</span></div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={copyLink} className="btn-ghost !py-2 !px-4 text-sm inline-flex items-center gap-2" data-testid="copy-referral"><Copy size={14} />Copy</button>
              <button onClick={shareWhatsApp} className="!py-2 !px-4 text-sm inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white font-semibold" data-testid="share-whatsapp"><MessageCircle size={14} />WhatsApp</button>
              <button onClick={nativeShare} className="btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-2" data-testid="share-native"><Share2 size={14} />Share</button>
            </div>
          </div>
        </div>

        {r.referred?.length > 0 && (
          <div className="border-t border-white/10 bg-white/5 text-white p-6 md:p-8">
            <div className="eyebrow" style={{ color: "var(--asure-gold)" }}>Friends you've referred</div>
            <div className="mt-4 grid md:grid-cols-2 gap-3">
              {r.referred.slice(0, 8).map((x, i) => (
                <div key={i} className="flex items-center justify-between text-sm bg-white/5 rounded-xl px-4 py-3 border border-white/10">
                  <div>
                    <div className="font-semibold">{x.first_name} · {x.city}</div>
                    <div className="text-xs text-white/70">{x.product_interest}</div>
                  </div>
                  <div className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${x.status === 'converted' ? 'bg-asure-gold text-asure-text' : 'bg-white/10 text-white'}`}>{x.status}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ icon: I, label, v, highlight }) {
  return (
    <div className={`rounded-2xl p-4 border ${highlight ? 'bg-asure-gold/15 border-asure-gold/40' : 'bg-white/5 border-white/10'}`}>
      <div className="flex items-center gap-2 eyebrow" style={{ color: highlight ? "var(--asure-gold)" : "rgba(255,255,255,0.6)" }}>
        <I size={12} />{label}
      </div>
      <div className="font-display text-4xl mt-2">{v}</div>
    </div>
  );
}
